import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  CircularProgress,
  Collapse,
  Input,
  Grid,
} from "@mui/material";
import { Link, useLocation, useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import AccountMenu from "../AccountMenu/AccountMenu";
import axios from "axios";
import { configuration } from "../../helper/appconfig";
import { connect } from "react-redux";
import { handleAuthTokenError } from "../../redux/actions/authActions";
import toastAlert from "../../helper/ToastAlert/ToastAlert";
import { DarkButton } from "../../common-styled";
import CompanyLogo from "../../pages/CompanyLogo/CompanyLogo";
import { sidebarLinks } from "./sidebarLinks";
import "./NewSidebar.css";

const drawerWidth = 260;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    display: "block",
    position: "fixed",
    zIndex: "99999999",
    width: "280px",
  },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: "68px",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0, 1),
  minHeight: "64px",
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open
    ? {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down("md")]: {
          width: "100%",
        },
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }
    : {
        width: `calc(100% - 68px)`,
        [theme.breakpoints.down("md")]: {
          width: "100%",
        },
      }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: open ? "normal" : "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const NewSidebarOptimized = ({ authData, children, handleAuthTokenError }) => {
  const [open, setOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const [searchString, setSearchString] = useState("");
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const handleDrawerOpen = () => setOpen(true);

  const handleDrawerClose = () => {
    setOpen(false);
    setOpenMenus({});
  };

  const toggleMenu = (menuId) => {
    if (!open) {
      setOpen(true);
      setTimeout(() => {
        setOpenMenus({ [menuId]: true });
      }, 200);
      return;
    }
    setOpenMenus((prev) => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [menuId]: !prev[menuId],
    }));
  };

  const handleSearchTextChange = (evt) => {
    setSearchString(evt?.target?.value);
    setError([]);
  };

  const searchLabs = () => {
    const url = configuration.searchLabs;
    const tokenType = authData?.tokenType;
    const idToken = authData?.idToken;
    const headers = {
      Authorization: `${tokenType} ${idToken}`,
    };
    const body = {
      search: searchString,
    };

    axios
      .post(url, body, { headers })
      .then((response) => {
        setLoading(false);
        if (response?.data?.length) {
          history.push({
            pathname: "/filteredLabs",
            state: { filteredLabData: response.data },
          });
        } else {
          toastAlert("No labs found with this title", "success");
        }
      })
      .catch((error) => {
        setLoading(false);
        let errorMessage = error?.response?.data?.message;
        if (errorMessage === "Unauthorized") {
          handleAuthTokenError({ authData, history });
        }
        toastAlert(
          errorMessage || "Something went wrong. Please try again later.",
          "error"
        );
      });
  };

  const handleSearch = () => {
    if (!searchString.trim()) {
      setError(["searchStringEmpty"]);
    } else {
      setError([]);
      setLoading(true);
      searchLabs();
    }
  };

  const handleOnKeyDown = (event) => {
    if (event.keyCode === 13) {
      handleSearch();
    }
  };

  const getAppBarTitle = () => {
    const pathName = location?.pathname;
    const titleMap = {
      "/dashboard": "Dashboard",
      "/userProfile": "User Profile",
      "/AllUser": "All Users",
      "/createuser": "Create User",
      "/createmultipleusers": "Create Multiple Users",
      "/createlab": "Create Lab",
      "/alllabs": "All Labs List",
      "/filteredLabs": "Filtered Labs",
      "/allPolicies": "All Policy List",
      "/allAttachedPolicies": "All Attached Policy List",
      "/createcategory": "Create Category",
      "/category/AllCategory": "All Category",
      "/level/AllLevels": "All Level",
      "/allUserGroups": "All User Groups",
      "/level/CreateLevels": "Create Level",
      "/allGroups": "All Group",
      "/attachPolicy": "Attach Policy",
      "/creategroup": "Create Group",
      "/createpolicy": "Create Policy",
      "/createUserGroup": "Create User Group",
      "/myCredit": "My Credits",
      "/Transactions": "Transactions",
      "/Payment": "Payment",
      "/labProvisionHistory": "Lab Provision History",
      "/myGroups": "My Groups",
      "/myAttachedPolicies": "My Attached Policies",
      "/generateCoupon": "Generate Coupon",
      "/couponList": "All Coupons",
      "/gradingHistory": "Grading History",
      "/certificate": "Certificates",
      "/certificateList": "Certificate List",
      "/certificateThemeSettings": "Theme Settings",
    };

    if (titleMap[pathName]) return titleMap[pathName];
    if (pathName.includes("/viewlab/")) return "View Lab";
    if (pathName.includes("/editlab/")) return "Edit Lab";
    if (pathName.includes("/editcategory/")) return "Edit Category";
    if (pathName.includes("/editlevel/")) return "Edit Level";
    if (pathName.includes("/editGroup/")) return "Edit Group";
    if (pathName.includes("/editUserGroup/")) return "Edit User Group";
    if (pathName.includes("/editAttachedPolicy/")) return "Edit Attached Policy";
    if (pathName.includes("/viewPolicy/")) return "View Policy";
    if (pathName.includes("/editpolicy/")) return "Edit Policy";
    if (pathName.includes("/groupUsers/")) return "Group Users";
    if (pathName.includes("/monitoring/live-user-list")) return "Live User List";
    if (pathName.includes("/view-ratings/")) return "View Rating";
    if (pathName.includes("/support")) return "Support";
    if (pathName.startsWith("/couponList/")) return "All Users of Coupon";
    if (pathName.includes("/alllabs/")) return location?.state?.labGroup;
    if (pathName.includes("/labGroup/")) return location?.state?.labGroup;
    if (pathName.includes("/groupPolicies/")) return location?.state?.groupName;

    return "";
  };

  const shouldShowSearch = () => {
    const pathName = location?.pathname;
    return (
      pathName === "/dashboard" ||
      pathName === "/filteredLabs" ||
      pathName.includes("/labGroup/") ||
      pathName.includes("/viewlab/")
    );
  };

  const shouldShowMenuItem = (item) => {
    const userRole = Cookies.get("userRole");
    if (!item.roles.includes(userRole)) return false;
    if (item.condition && !item.condition(Cookies)) return false;
    return true;
  };

  const isActive = (item) => {
    const pathName = location?.pathname;
    if (item.path && pathName === item.path) return true;
    if (item.subMenu) {
      return item.subMenu.some((sub) => pathName === sub.path);
    }
    return false;
  };

  const isSubActive = (subItem) => {
    return location?.pathname === subItem.path;
  };

  const renderMenuItem = (item) => {
    if (!shouldShowMenuItem(item)) return null;

    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const isOpen = openMenus[item.id] || false;
    const active = isActive(item);

    if (!hasSubMenu && item.path) {
      return (
        <Link to={{ pathname: item.path }} key={item.id} className="ck-sidebar-link">
          <div className={`ck-sidebar-item ${active ? 'active' : ''}`}>
            <span className="ck-sidebar-icon">{item.icon}</span>
            {open && <span className="ck-sidebar-text">{item.name}</span>}
          </div>
        </Link>
      );
    }

    if (hasSubMenu) {
      const userRole = Cookies.get("userRole");
      const filteredSubMenu = item.subMenu.filter((subItem) =>
        subItem.roles.includes(userRole)
      );
      if (filteredSubMenu.length === 0) return null;

      return (
        <div key={item.id} className="ck-sidebar-group">
          <div
            className={`ck-sidebar-item ${active ? 'active' : ''}`}
            onClick={() => toggleMenu(item.id)}
          >
            <span className="ck-sidebar-icon">{item.icon}</span>
            {open && (
              <>
                <span className="ck-sidebar-text">{item.name}</span>
                <span className={`ck-sidebar-arrow ${isOpen ? 'open' : ''}`}>
                  <ExpandMoreIcon fontSize="small" />
                </span>
              </>
            )}
          </div>
          {open && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <div className="ck-sidebar-submenu">
                {filteredSubMenu.map((subItem) => (
                  <Link
                    to={subItem.path}
                    key={`${item.id}-${subItem.id}`}
                    className="ck-sidebar-link"
                  >
                    <div className={`ck-sidebar-subitem ${isSubActive(subItem) ? 'active' : ''}`}>
                      <span className="ck-sidebar-dot"></span>
                      <span className="ck-sidebar-subtext">{subItem.name}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </Collapse>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <Grid container>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Box sx={{ display: "flex" }} className="ck-layout">
          <CssBaseline />
          {/* AppBar */}
          <AppBar className="ck-appbar" position="fixed" open={open}>
            <Toolbar style={{ alignItems: "center" }}>
              <Grid container style={{ alignItems: "center" }}>
                <Grid item lg={6} sm={3} md={5} xs={5}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      color="inherit"
                      aria-label="toggle drawer"
                      onClick={open ? handleDrawerClose : handleDrawerOpen}
                      edge="start"
                      className="ck-menu-toggle"
                    >
                      {open ? <ChevronLeftIcon /> : <MenuIcon />}
                    </IconButton>
                    <Typography
                      className="ck-appbar-title"
                      variant="h6"
                      noWrap
                      component="div"
                    >
                      {getAppBarTitle()}
                    </Typography>
                  </div>
                </Grid>
                <Grid
                  item
                  lg={6}
                  sm={9}
                  md={7}
                  xs={7}
                  style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {shouldShowSearch() && (
                      <div className="ck-search-wrapper">
                        <div className="ck-search-box">
                          <Input
                            className="ck-search-input"
                            onChange={handleSearchTextChange}
                            fullWidth
                            placeholder="Search labs..."
                            size="small"
                            type="search"
                            onKeyDown={handleOnKeyDown}
                            disableUnderline
                          />
                          <button className="ck-search-btn" onClick={handleSearch}>
                            {loading ? (
                              <CircularProgress sx={{ color: "#fff" }} size={18} />
                            ) : (
                              <SearchIcon fontSize="small" />
                            )}
                          </button>
                        </div>
                        {error.includes("searchStringEmpty") && (
                          <span className="ck-search-error">Please enter a search term</span>
                        )}
                      </div>
                    )}

                    <div className="ck-user-section">
                      <div className="ck-user-info">
                        <Typography className="ck-user-name" variant="body2" noWrap>
                          {(authData?.firstName || Cookies.get("firstName")) +
                            " " +
                            (authData?.lastName || Cookies.get("lastName"))}
                        </Typography>
                        <Typography className="ck-user-email" variant="caption" noWrap>
                          {authData?.userEmail || Cookies.get("email")}
                        </Typography>
                      </div>
                      <AccountMenu />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </Toolbar>
          </AppBar>

          {/* Sidebar Drawer */}
          <Drawer
            className={`ck-drawer ${!open ? 'ck-drawer-collapsed' : ''}`}
            variant="permanent"
            open={open}
          >
            <DrawerHeader className="ck-drawer-header">
              {open ? (
                <CompanyLogo />
              ) : (
                <img
                  src={authData.logo}
                  alt="logo"
                  className="ck-logo-small"
                  onClick={handleDrawerOpen}
                />
              )}
            </DrawerHeader>

            <div className="ck-sidebar-nav">
              {sidebarLinks.map((item) => renderMenuItem(item))}
            </div>
          </Drawer>

          {/* Main Content */}
          <Box component="main" className="ck-main-content">
            <DrawerHeader />
            {children}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default connect(mapStateToProps, { handleAuthTokenError })(
  NewSidebarOptimized
);
